class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    /* Ancona V5 (Agent D, P0-15): monotonic request token — a superseded
       (slower, older) predictive response can never overwrite newer results. */
    this.requestToken = 0;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a, button.predictive-search__retry[aria-selected="true"]')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // APEX P0.5: shopper input must never be compiled as a regular expression
      // (characters like [ ( \ * ? threw here, and a zero-match .match() returned
      // null and crashed on .length). Count occurrences with plain string
      // splitting and replace only when the previous term appears exactly once.
      if (!previousTerm || currentButtonText.split(previousTerm).length - 1 !== 1) {
        return;
      }
      // Function replacement so $-patterns in shopper input are never
      // interpreted as replacement directives.
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    let selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null && !element.disabled && element.getAttribute('aria-disabled') !== 'true'
    );
    let activeElementIndex = 0;
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    // A loading/error state can have no available choices. A formerly selected
    // option may also have become hidden or disabled during a request.
    if (selectedElement && selectedElementIndex === -1) {
      selectedElement.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      selectedElement = null;
    }
    if (!allVisibleElements.length || (moveUp && !selectedElement)) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // APEX P0.5: the previous key replaced only the first space with a hyphen,
    // colliding distinct queries such as "a-b" and "a b". The lowercased term
    // itself is a collision-free key (case-insensitive sharing is intended —
    // Shopify search is case-insensitive).
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    /* Ancona V5 (Agent D, P0-15): every new query supersedes the previous
       one — abort the in-flight request and advance the token so any
       response that still resolves is dropped before touching the DOM. */
    this.requestToken += 1;
    const requestToken = this.requestToken;
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search failed with status ${response.status}`);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        /* Ancona V5 (P0-15): stale responses populate the cache only. */
        if (requestToken !== this.requestToken) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // The call was aborted (superseded by a newer query or a reset)
          return;
        }
        if (requestToken === this.requestToken) this.renderErrorState();
        /* The failure is now represented in the visible/live state and has an
           in-place retry. Do not rethrow and create an unhandled rejection. */
        console.error(error);
      });
  }

  /* Atelier 360 pass 2: localized, visible, announced error state. Text comes
     from the element's data-error-text attribute (t-filtered in Liquid); the
     node is built with DOM APIs, never innerHTML. The next successful render
     replaces it. */
  renderErrorState() {
    const errorText = this.getAttribute('data-error-text') || 'Search is unavailable right now. Press Enter to search the full store instead.';
    const retryText = this.getAttribute('data-retry-text') || 'Search';
    this.predictiveSearchResults.textContent = '';
    this.input.setAttribute('aria-activedescendant', '');
    const errorGroup = document.createElement('div');
    errorGroup.className = 'predictive-search__error-group';
    const errorElement = document.createElement('p');
    errorElement.className = 'predictive-search__error';
    errorElement.setAttribute('role', 'status');
    errorElement.textContent = errorText;
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'predictive-search__retry predictive-search__item button button--secondary';
    PredictiveSearch.retryOptionCount = (PredictiveSearch.retryOptionCount || 0) + 1;
    retryButton.id = 'predictive-search-retry-' + PredictiveSearch.retryOptionCount;
    retryButton.textContent = retryText;
    retryButton.addEventListener('click', () => {
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm) {
        this.input.focus();
        return;
      }
      retryButton.disabled = true;
      retryButton.setAttribute('aria-busy', 'true');
      this.getSearchResults(currentSearchTerm);
    }, { once: true });
    errorGroup.append(errorElement, retryButton);
    this.predictiveSearchResults.appendChild(errorGroup);
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.setLiveRegionText(errorText);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.input.setAttribute('aria-activedescendant', '');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      // Clearing/resetting supersedes pending work even if abort races with an
      // already fulfilled response. It must never reopen an empty search.
      this.requestToken += 1;
      this.abortController.abort();
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
